import { useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from 'wouter'
import queryString from 'query-string'

import queryClient from '@/queryClient'
import {
  listDocumentPages,
  getDocumentPage,
  deleteDocumentPage,
  createDocumentPage,
} from '@/lib/api/client/documentPages'
import { DocumentPage, updateDocumentPage } from '@/lib/api/client'
import QUERY_KEYS from './queryKeys'
import MUTATION_KEYS from './mutationKeys'
import { useToast } from '@/components/ui/use-toast'
import CustomError from '@/lib/api/CustomError'
import { convertDocumentToStep } from '../operation_steps/convertDocumentPageToStep'
import { useDefaultDocumentThemeConfig } from '../document_themes'
import { useDocumentThemeFlag } from '@/featureFlags/useDocumentThemeFlag'
import { useCreateTemplateAttribute } from '../template_attributes'
import { useCADPageStore } from '@/state'
import type { Step } from '../operation_steps/types'
import { useGetLatestDocumentVersion } from '../document_versions'

export const listDocumentPagesQuery = ({
  documentVersionId,
}: {
  documentVersionId?: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
    queryFn: async () => {
      if (!documentVersionId) return []
      const docs = await listDocumentPages(documentVersionId)
      return docs
    },
  })
}

export const useListDocumentPagesQuery = ({
  documentVersionId,
}: {
  documentVersionId?: string | null
}) => {
  const { toast } = useToast()
  return useQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
    queryFn: async () => {
      try {
        const docs = documentVersionId
          ? await listDocumentPages(documentVersionId)
          : []
        return docs
      } catch (e: any) {
        if (e instanceof CustomError) {
          toast({
            title: 'Error',
            description: e.message,
            variant: 'destructive',
          })
        }
        throw e
      }
    },
    enabled: Boolean(documentVersionId),
  })
}

export const useUpdateDocumentPage = (props?: {
  onSuccess?: (response?: DocumentPage | Array<DocumentPage>) => void
}) => {
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_DOCUMENT_PAGE],
    mutationFn: async ({
      documentPageId,
      values,
    }: {
      documentPageId: string
      values: {
        name: string
        template_values: any
        parent_id: string | null
      }
    }) => updateDocumentPage(documentPageId, values),
    onSuccess: async (documentPage) => {
      queryClient.setQueryData(
        [
          QUERY_KEYS.DOCUMENT_PAGES,
          { documentVersionId: documentPage?.document_version },
        ],
        (documentPages: Array<DocumentPage> | undefined) => {
          if (documentPages)
            return documentPages.map((currentDocumentPage) =>
              currentDocumentPage.id === documentPage?.id
                ? documentPage
                : currentDocumentPage,
            )
        },
      )
      if (props?.onSuccess) props.onSuccess(documentPage)
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const getDocumentPageQuery = ({
  documentPageId,
}: {
  documentPageId: string
}) => {
  return queryClient.fetchQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_PAGE, { documentPageId }],
    queryFn: () => getDocumentPage(documentPageId),
  })
}

export const useDeleteDocumentPage = (props?: { onSuccess?: () => void }) => {
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_DOCUMENT_PAGE],
    mutationFn: async ({ documentPageId }: { documentPageId: string }) =>
      deleteDocumentPage(documentPageId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, {}],
      })

      if (typeof props?.onSuccess === 'function') {
        props.onSuccess()
      }
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useCreateDocumentPage = ({
  invalidateOnSuccess = true,
}: {
  invalidateOnSuccess?: boolean
}) => {
  const {
    data: { isEnabled: isDocumentThemeFlagEnabled },
  } = useDocumentThemeFlag()

  const {
    data: { defaultTemplateAttributes },
  } = useDefaultDocumentThemeConfig()

  const { mutate: createTemplateAttribute } = useCreateTemplateAttribute()

  const subAssemblyDocumentPageId = useCADPageStore(
    (state) => state.subAssemblyDocumentPageId || null,
  )

  const { toast } = useToast()

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_DOCUMENT_PAGE],
    mutationFn: async ({
      documentVersionId,
      orderNumber,
      values,
    }: {
      documentVersionId: string
      orderNumber: number
      values: {
        name: string
        template_values: any
        parent_id: string | null
      }
    }) => {
      try {
        values.template_values = {
          ...(values.template_values || {}),
          created_using_document_theme: isDocumentThemeFlagEnabled,
        }
        const documentPage = await createDocumentPage(documentVersionId, values)

        const documentPageId = documentPage.id

        if (!documentPageId) {
          throw new Error('Failed to create new document')
        }

        if (isDocumentThemeFlagEnabled) {
          const skipDataTypesOnCreate = ['views']

          defaultTemplateAttributes.forEach((attr) => {
            if (skipDataTypesOnCreate.includes(attr.data_type)) {
              return
            }
            createTemplateAttribute({
              documentPageId,
              payload: {
                data_type: attr.data_type,
                template_values: attr.template_values,
              },
            })
          })
        }

        return {
          documentPage: convertDocumentToStep(documentPage),
          documentVersionId,
          orderNumber,
        }
      } catch (error: any) {
        if (error instanceof CustomError) {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        }
        return {
          documentVersionId,
          orderNumber,
        }
      }
    },
    onMutate: async ({ documentVersionId, orderNumber }) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
      })

      const previousDocumentPages = queryClient.getQueryData([
        QUERY_KEYS.DOCUMENT_PAGES,
        { documentVersionId },
      ])

      const stepPlaceholder: Step = {
        name: '',
        parent: subAssemblyDocumentPageId,
        document_version: documentVersionId,
        order_number: orderNumber,
        documentpageassemblygroupids: [],
        template_values: {},
        assembly_group_ids: [],
      }

      queryClient.setQueryData<Step[]>(
        [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        (old = []) => {
          const next = [...old]
          // ensure index is within bounds
          const idx = Math.min(Math.max(orderNumber - 1, 0), next.length)
          next.splice(idx, 0, stepPlaceholder)
          return next
        },
      )
      return { previousDocumentPages }
    },
    onError: (_, { documentVersionId }, context) => {
      queryClient.setQueryData(
        [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        context?.previousDocumentPages,
      )
    },
    onSuccess: async ({ documentVersionId }) => {
      if (invalidateOnSuccess) {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.DOCUMENT_PAGES, { documentVersionId }],
        })
      }
    },
  })
}

export const useListCurrentDocumentPages = () => {
  const { documentId } = useParams<{
    documentId: string
  }>()

  const search = useSearch()
  const queryParams = useMemo(
    () =>
      queryString.parse(search) as {
        dv?: string // document version
      },
    [search],
  )

  const { data: latestDocumentVersion, isLoading: isLoadingDocumentVersion } =
    useGetLatestDocumentVersion({
      documentId: !queryParams.dv ? documentId : undefined,
    })

  const documentVersionId = queryParams.dv || latestDocumentVersion?.id

  const { data: documentPages, isLoading: isLoadingDocumentPages } =
    useListDocumentPagesQuery({
      documentVersionId,
    })

  return {
    documentPages: documentPages || [],
    isLoading: isLoadingDocumentVersion || isLoadingDocumentPages,
  }
}

export { QUERY_KEYS, MUTATION_KEYS }
export { useToggleSubpageVisibility } from './useToggleVisibility'
export { useMoveSubpage } from './useMoveSubpage'
